/**
 * HomeFAQ — single source of truth for the homepage FAQ block.
 *
 * Rendered identically on:
 *   - /            (Home.js, post-launch homepage)
 *   - /            (ComingSoon.js, pre-launch landing page)
 *
 * The visible <dl> is mirrored into FAQPage JSON-LD in each parent's
 * SEOHead schema graph via the exported HOMEPAGE_FAQS array — so visitors
 * AND LLM snippet engines (Google, ChatGPT, Perplexity, Gemini) see the
 * same canonical answers. Keep answers <320 chars so they can be quoted
 * in full.
 *
 * WhyIQ audit Phase 3 fix: "Answer Clarity" was scoring 28/100 because we
 * had no canonical Q&A blocks. This component is that fix in one place.
 */
import React from 'react';

export const HOMEPAGE_FAQS = [
  {
    question: 'What is theailedger.ai?',
    answer:
      'theailedger.ai is a curated marketplace for AI-powered products across 52 categories — AI agents, chatbots, automation tools, prompts, software, and digital assets. Creators list their AI tools and earn from buyers worldwide; buyers discover vetted AI products built to generate real income.',
  },
  {
    question: 'How much does it cost to sell on theailedger.ai?',
    answer:
      'Founding Creators pay 0% in marketplace fees for the first 6 months, then 5% per sale after that. There is no monthly subscription, no listing fee, and no upfront cost — we only earn when you do.',
  },
  {
    question: 'How do payouts work for creators?',
    answer:
      'Creators connect a Stripe Connect account and receive payouts directly to their bank in 80+ countries. Funds settle on a rolling 2-day basis after each sale. No platform-side holds, no minimum balance.',
  },
  {
    question: 'How are tools vetted?',
    answer:
      'Every listing is screened by an automated quality pipeline (duplicate detection, broken-link checks, similarity scoring against existing tools) and a human reviewer before going live. We reject submissions that look spammy or copy other creators.',
  },
  {
    question: 'Is theailedger.ai real? Where has it been covered?',
    answer:
      'Yes. theailedger.ai was covered across CBS, NBC, FOX, and ABC network affiliates plus 300+ syndicated business news outlets on March 25, 2026. See the full press list at theailedger.ai/press.',
  },
];

/**
 * @param {object}   props
 * @param {Array}    [props.faqs]          Defaults to HOMEPAGE_FAQS. Pages can pass a superset (e.g. pre-launch ComingSoon adds 2 launch-specific Qs).
 * @param {function} props.onFullSetClick  Routes to /faqs (parents pass their navigate handler so we don't import useNavigate here).
 */
export default function HomeFAQ({ faqs = HOMEPAGE_FAQS, onFullSetClick }) {
  return (
    <section
      id="faq"
      className="px-4 sm:px-6 py-16 border-t border-white/5"
      data-testid="homepage-faq"
      aria-labelledby="homepage-faq-heading"
    >
      <div className="max-w-3xl mx-auto">
        <h2
          id="homepage-faq-heading"
          className="text-3xl sm:text-4xl font-bold text-center mb-3"
        >
          Common questions
        </h2>
        <p className="text-center text-neutral-400 text-sm mb-10">
          Quick answers — see the full set in the{' '}
          <button
            type="button"
            onClick={onFullSetClick}
            className="text-[#00F0FF] hover:underline"
            data-testid="homepage-faq-fullset-link"
          >
            FAQ section
          </button>
          .
        </p>
        <dl className="space-y-3">
          {faqs.map((faq, idx) => (
            <details
              key={faq.question}
              className="group bg-neutral-900/50 border border-white/10 hover:border-white/20 rounded-xl transition-colors overflow-hidden"
              data-testid={`homepage-faq-item-${idx}`}
              {...(idx === 0 ? { open: true } : {})}
            >
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 select-none">
                <dt className="text-base font-semibold text-white">{faq.question}</dt>
                <span
                  className="text-[#00F0FF] text-xl flex-shrink-0 transition-transform group-open:rotate-45"
                  aria-hidden="true"
                >
                  +
                </span>
              </summary>
              <dd className="px-5 pb-5 text-sm text-neutral-300 leading-relaxed">
                {faq.answer}
              </dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  );
}
